export type ImageSize = { w: number; h: number };

/** Видимая область доски: размер в мировых единицах и экранных пикселей на единицу. */
export type VisibleArea = ImageSize & { scale?: number };

/**
 * Размер картинки при вставке на доску.
 *
 * Раньше картинка ставилась «пиксель в пиксель» и ужималась, только если не
 * влезала. На отдалённой камере такая картинка выглядела крошечной: мерили в
 * мировых единицах, а видит человек экранные. Теперь целимся в долю видимой
 * области — тогда картинка всегда попадает на доску удобного размера, на каком
 * бы масштабе ни стояла камера. Мелкие увеличиваем, но не бесконечно: выше
 * MAX_UPSCALE начинается мыло.
 */

/** Занять примерно столько от видимой области по «узкой» стороне. */
const TARGET_FRACTION = 0.45;
/** Больше этой доли не растягиваем — иначе картинка перекрывает всё вокруг. */
const MAX_FRACTION = 0.8;
/**
 * Предел растяжения мелких картинок — в ЭКРАННЫХ пикселях, не в мировых.
 * Мыло зависит от того, во сколько раз картинка растянута на экране, поэтому на
 * отдалённой камере в мировых единицах можно растянуть сильнее: экранная
 * плотность останется та же.
 */
const MAX_SCREEN_UPSCALE = 2.5;
const MIN_SIDE = 40;
const HARD_MAX_SIDE = 4096;
const FALLBACK: ImageSize = { w: 200, h: 150 };

function positive(v: unknown): number | null {
  return typeof v === "number" && Number.isFinite(v) && v > 0 ? v : null;
}

function clamp(v: number, lo: number, hi: number): number {
  return Math.min(hi, Math.max(lo, v));
}

export function imageInsertSize(
  natural: Partial<ImageSize> | null | undefined,
  visible: VisibleArea | null | undefined
): ImageSize {
  const w0 = positive(natural?.w) ?? FALLBACK.w;
  const h0 = positive(natural?.h) ?? FALLBACK.h;

  const visW = positive(visible?.w);
  const visH = positive(visible?.h);
  const cameraScale = positive(visible?.scale) ?? 1;

  const maxW = clamp(visW ? visW * MAX_FRACTION : 1200, 320, HARD_MAX_SIDE);
  const maxH = clamp(visH ? visH * MAX_FRACTION : 900, 240, HARD_MAX_SIDE);
  const targetW = visW ? visW * TARGET_FRACTION : 640;
  const targetH = visH ? visH * TARGET_FRACTION : 480;

  let scale = 1;

  // Мелкая картинка: подрастает до заметного размера.
  const maxUpscale = MAX_SCREEN_UPSCALE / clamp(cameraScale, 0.05, 20);
  const grow = Math.min(targetW / w0, targetH / h0);
  if (grow > 1) scale = Math.min(grow, Math.max(1, maxUpscale));

  // Крупная: ужимается, чтобы влезть в видимую область.
  const shrink = Math.min(maxW / (w0 * scale), maxH / (h0 * scale));
  if (shrink < 1) scale *= shrink;

  // Минимум держим масштабом, а не сторонами по отдельности — иначе у узкой
  // картинки одна сторона упрётся в MIN_SIDE раньше другой и пропорции поедут.
  scale = Math.max(scale, Math.max(MIN_SIDE / w0, MIN_SIDE / h0));

  return { w: Math.round(w0 * scale), h: Math.round(h0 * scale) };
}
